<template>
  <div
    class="contact"
    :class="contactClasses"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <div class="contact-selection">
      <Checkbox
        dense
        :disabled="contactOptedOut && !isOutreachContactListsRoute"
        :model-value="isChecked"
        new-design
        @update:model-value="checkboxClicked($event)"
      />
    </div>

    <OutreachContactAvatar :contact="contact" />

    <div class="contact-details-wrapper">
      <div class="contact-details">
        <div class="contact-header">
          <div class="contact-header-name-wrapper">
            <div class="contact-header-name">
              <BaseIcon
                v-if="isPrivate"
                class="contact-lock"
                icon="lock"
              />
              <div
                v-if="type === 'outlet_desks' || type === 'private_outlets'"
                class="text-bold"
              >
                {{ contact.name }}
              </div>
              <a
                v-else
                @click="nameClicked"
              >
                {{ contactTitle }}
              </a>

              <div
                v-if="
                  isHovering && !contactOptedOut && !isPrivate && !hidePreview
                "
                style="display: inline-flex"
              >
                <BaseButton
                  class="contact-preview-button"
                  color="secondary"
                  icon="expand-preview"
                  :label="getLocaleText('outreach_contact.preview')"
                  new-design
                  new-icon
                  size="sm"
                  @click="previewClicked"
                />
              </div>
            </div>

            <OutreachContactOptedOut
              v-if="contactOptedOut"
              :outlet="contactPrimaryOutlet"
            />
          </div>
        </div>

        <div
          v-if="type === 'outlet_desks'"
          class="contact-info"
        >
          <a @click="nameClicked">
            {{ (contact as OutletDesk).outlet.name }}
          </a>
        </div>

        <div class="contact-info">
          <template v-if="!contactOptedOut">
            <div
              v-if="contactOutletType"
              class="contact-stat"
            >
              <span class="contact-stat-outlet-type" />
              {{ contactOutletType }}
            </div>

            <div
              v-if="contactCirculation"
              class="contact-stat"
            >
              <QIcon
                name="ion-ios-people"
                size="1rem"
              />
              <span>{{ contactCirculation }}</span>
              <StreemTooltip>
                {{ contactCirculationTooltip }}
              </StreemTooltip>
            </div>
          </template>

          <div
            v-if="contactRole"
            class="contact-stat contact-stat-role"
          >
            <QIcon
              name="far fa-address-card"
              size=".8rem"
            />
            <EllipseContent>
              {{ contactRole }}
            </EllipseContent>
          </div>

          <div
            v-if="contactLocation && !contactOptedOut"
            class="contact-stat contact-stat-location"
          >
            <QIcon
              name="ion-ios-pin"
              size=".8rem"
            />
            <span>{{ contactLocation }}</span>
          </div>
        </div>

        <template v-if="!contactOptedOut">
          <div
            v-if="contactOutlets.length"
            class="contact-info contact-list-outlets"
          >
            <BaseIcon icon="outlet" />
            <OutreachContactListItems
              clickable
              :items="contactOutlets"
              :title="getLocaleText('outreach_contact.outlets')"
              @click="outletClicked"
            />
          </div>

          <div
            v-if="contactTopics.length"
            class="contact-info contact-list-sectors"
          >
            <div class="contact-list-sectors__title">
              {{ getLocaleText("outreach_contact.sectors") }}:
              <StreemTooltip>
                <div class="q-pa-sm">
                  {{ getLocaleText("outreach_contact.sectors_tooltip") }}
                </div>
              </StreemTooltip>
            </div>
            <OutreachContactListItems
              :clickable="clickableTopics"
              force-tooltip
              :items="contactTopics"
              :title="getLocaleText('outreach_contact.sectors')"
              @click="topicClicked"
            >
              <template
                v-if="clickableTopics"
                #itemTooltip="{ item, isEllipsed }"
              >
                <template v-if="isEllipsed">
                  {{ getLocaleText("outreach_contact.filter_by") }}
                  {{ item.name }}
                </template>
                <template v-else>
                  {{ getLocaleText("outreach_contact.filter_by_sector") }}
                </template>
              </template>

              <template
                v-if="clickableTopics"
                #itemPopoverTooltip
              >
                {{ getLocaleText("outreach_contact.filter_by_sector") }}
              </template>
            </OutreachContactListItems>
          </div>
        </template>
      </div>

      <div
        v-if="!hideExtras"
        class="contact-details-extra"
      >
        <template v-if="!contactOptedOut">
          <OutreachContactSocialStats
            v-if="type === 'influencers'"
            :contact="contact"
          />

          <OutreachContactSocials
            v-else
            class="contact-socials"
            :emails="contactEmails"
            :homepages="(contact as Outlet).homepages"
            :invalid-email="contact.invalidEmail"
            :phones="contactPhones"
            :socials="(contact as Outlet).socials"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  EllipseContent,
  StreemTooltip,
} from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import OutreachContactAvatar from "shared/components/Outreach/OutreachContact/OutreachContactAvatar.vue";
import OutreachContactListItems from "shared/components/Outreach/OutreachContact/OutreachContactListItems.vue";
import OutreachContactOptedOut from "shared/components/Outreach/OutreachContact/OutreachContactOptedOut.vue";
import OutreachContactSocials from "shared/components/Outreach/OutreachContact/OutreachContactSocials.vue";
import OutreachContactSocialStats from "shared/components/Outreach/OutreachContact/OutreachContactSocialStats.vue";
import useContact, { type ContactType } from "shared/composables/useContact";
import {
  type Outlet,
  type OutletDesk,
  type OutletsTopic,
} from "shared/resources";

interface OutreachContactProps {
  clickableTopics?: boolean;
  contact: ContactType;
  hideExtras?: boolean;
  hidePreview?: boolean;
  isChecked?: boolean;
  isPrivate?: boolean;
}

const props = defineProps<OutreachContactProps>();

const emit = defineEmits(["click", "topic", "checkbox-clicked", "preview"]);

const {
  type,
  contactClass,
  contactTitle,
  contactLocation,
  contactOutletType,
  contactRole,
  contactTopics,
  contactCirculation,
  contactEmails,
  contactPhones,
  contactCirculationTooltip,
  contactOutlets,
  contactPrimaryOutlet,
  contactOptedOut,
} = useContact(props);

const isHovering = ref(false);

const route = useRoute();

const isOutreachContactListsRoute = computed(
  () => route.name === "outreach-lists-contacts"
);

const contactClasses = computed(() => {
  const items = [contactClass.value, `contact-${type.value}`];

  if (contactOptedOut.value && !isOutreachContactListsRoute.value) {
    items.push("contact-disabled");
  }

  if (props.isChecked) {
    items.push("contact-checked");
  }

  return items;
});

function nameClicked() {
  if (contactOptedOut.value) return;

  if (Number(props.contact.id) === 0) return;

  emit("click", props.contact, type.value);
}

function topicClicked(topic: OutletsTopic) {
  emit("topic", topic, type.value);
}

function outletClicked(outlet: Outlet) {
  emit("click", outlet, "outlets");
}

function checkboxClicked(checked: boolean) {
  emit("checkbox-clicked", props.contact, checked);
}

function previewClicked() {
  emit("preview", props.contact, type.value);
}
</script>

<style lang="scss" scoped>
@import "shared/css/outreach";

.contact {
  padding-inline: var(--s-spacing-lg);
  padding-block: var(--s-spacing-lg);
  display: flex;
  gap: var(--s-spacing-lg);
  background-color: white;
  position: relative;
  min-height: 72px;

  &-disabled {
    .checkbox,
    .c-outreach-contact-avatar,
    .contact-header-name,
    .contact-info {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  a {
    color: var(--s-color-denim-9);
    cursor: pointer;

    &:hover {
      color: var(--s-title-hover);
    }
  }

  &-preview-button {
    font-weight: 500;

    --c-button-color: var(--s-color-denim-9);
    --c-button-border-radius: var(--s-border-radius-md);
    --c-button-font-size: var(--s-font-size-md);
    --c-button-padding-inline: 14px;
    --c-button-padding-block: 8px;
  }

  &-selection {
    display: flex;
    align-items: start;
  }

  &-socials {
    opacity: 0;
    transition: 150ms opacity;
  }

  &-details-wrapper {
    display: flex;
    gap: var(--s-spacing-lg);
    flex: 1;
    justify-content: space-between;
  }

  &-details {
    gap: var(--s-spacing-sm);
    display: flex;
    flex-direction: column;
    flex-shrink: 1;
    min-width: 300px;
    justify-content: center;
    overflow: hidden;
  }

  &-details-extra {
    flex-shrink: 0;
  }

  &-header {
    gap: var(--s-spacing-md);
    display: flex;
    justify-content: space-between;
    align-items: start;

    &-name-wrapper {
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);
    }

    &-name {
      flex: 1;
      min-height: 32px;
      display: flex;
      align-items: center;
      gap: var(--s-spacing-md);

      a {
        color: var(--s-color-blue-9);
        line-height: 150%;
        font-weight: 700;

        &:hover {
          color: var(--s-color-blue-5);
        }
      }
    }

    &-role {
      font-weight: bold;
      color: var(--s-color-denim-7);
    }
  }

  &-lock {
    color: var(--s-color-denim-9);
  }

  &-info {
    gap: var(--s-spacing-md);
    display: flex;
    align-items: center;
  }

  &-list-outlets,
  &-list-sectors {
    color: var(--s-color-denim-6);
    gap: var(--s-spacing-sm);
    font-size: var(--s-font-size-md);

    &__title {
      color: var(--s-color-denim-7);
      line-height: 150%;
      text-decoration: dotted underline;
      font-weight: 500;
    }
  }

  &-stat {
    gap: var(--s-spacing-sm);
    display: flex;
    align-items: center;
    color: var(--s-color-denim-6);

    &-outlet {
      overflow: hidden;
    }

    &-role {
      max-width: 200px;
      overflow: hidden;
      padding-left: 1px;
    }

    &-location {
      min-width: fit-content;
    }

    &-outlet-type {
      background-color: var(--c-outreach-contact-color);
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
  }

  &:hover {
    background-color: var(--s-color-denim-1);

    .contact-socials {
      opacity: 1;
    }
  }

  &-checked {
    .contact-add-button {
      opacity: 1;
    }
  }
}
</style>
